import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

const STORAGE_KEY = 'ember.ageVerified';

interface AgeVerificationRecord {
  verified: true;
  timestamp: number;
}

function readVerification(): AgeVerificationRecord | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  // Format actuel : JSON { verified, timestamp }. Format legacy : "true".
  if (raw === 'true') {
    return { verified: true, timestamp: Date.now() };
  }
  try {
    const parsed = JSON.parse(raw) as Partial<AgeVerificationRecord>;
    if (parsed.verified === true && typeof parsed.timestamp === 'number') {
      return { verified: true, timestamp: parsed.timestamp };
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Hook utilitaire : expose l'etat de verification d'age + accept/decline.
 * Persiste { verified: true, timestamp } dans localStorage sous 'ember.ageVerified'.
 */
export function useAgeGate(): {
  verified: boolean;
  accept: () => void;
  decline: () => void;
} {
  const [verified, setVerified] = useState<boolean>(() => readVerification() !== null);

  useEffect(() => {
    // Re-sync si un autre onglet modifie le storage.
    function onStorage(event: StorageEvent) {
      if (event.key === STORAGE_KEY) {
        setVerified(readVerification() !== null);
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const accept = useCallback(() => {
    const record: AgeVerificationRecord = { verified: true, timestamp: Date.now() };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    setVerified(true);
  }, []);

  const decline = useCallback(() => {
    window.localStorage.removeItem(STORAGE_KEY);
    setVerified(false);
  }, []);

  return { verified, accept, decline };
}

interface AgeGateProps {
  onAccept: () => void;
  onDecline: () => void;
}

/**
 * Modale plein ecran de verification d'age (18+).
 * S'affiche au premier acces, persiste le choix via useAgeGate.
 * Le wiring (afficher / cacher) se fait dans l'app shell par le caller.
 */
export function AgeGate({ onAccept, onDecline }: AgeGateProps) {
  function handleAccept() {
    const record: AgeVerificationRecord = { verified: true, timestamp: Date.now() };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    onAccept();
  }

  function handleDecline() {
    onDecline();
    // Redirection de sortie : on envoie vers google, fallback simple si bloque.
    window.location.replace('https://www.google.com');
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="age-gate-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm p-4"
    >
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <CardTitle id="age-gate-title" className="text-2xl">
            Tu as 18 ans ou plus ?
          </CardTitle>
          <CardDescription>
            Ember contient du contenu pour adultes (questions et défis à caractère intime et
            sexuel). En entrant, tu certifies sur l'honneur avoir 18 ans ou plus.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-3">
          <Button size="lg" className="w-full" onClick={handleAccept}>
            Je certifie avoir 18 ans ou plus
          </Button>
          <Button size="lg" variant="outline" className="w-full" onClick={handleDecline}>
            Quitter
          </Button>
        </CardContent>

        <CardFooter className="flex flex-col gap-2 text-center text-xs text-muted-foreground">
          <p>
            En continuant, tu acceptes nos{' '}
            <Link to="/legal/cgu" className="underline hover:text-foreground">
              CGU
            </Link>{' '}
            et notre{' '}
            <Link to="/legal/confidentialite" className="underline hover:text-foreground">
              Politique de confidentialité
            </Link>
            .
          </p>
          <p className="italic">Beta gratuite - réservée aux personnes majeures.</p>
        </CardFooter>
      </Card>
    </div>
  );
}
